import React, { useEffect, useState } from 'react'
import withTranslation from 'Common/withTranslation';
import { WithTranslationProps } from 'Common/Components/Translation/WithTranslationProps';
import { AccountInformationEditorProps } from './AccountInformationEditorProps';
import Subtitle from 'Common/Components/Titles/Subtitle';
import { X } from 'lucide-react';
import LoadingButton from 'Common/Components/LoadingOverlay/LoadingButton/LoadingButton';
import withMessages, { WithMessagesProps } from 'Common/withMessages';
import withNotifications, { WithNotificationsProps } from 'Common/withNotifications';
import { MessageType } from 'Common/Components/Messages/messageTypes';
import { NotificationType } from 'Common/Components/Notifications/notificationTypes';
import { axios_helper_async } from 'helpers/Axios';
import { PasswordEditorViewModel } from 'Common/ViewModels/UserProfile/PasswordEditor/PasswordEditorViewModel';
import { PasswordEditorErrorsViewModel } from 'Common/ViewModels/Errors/UserProfile/PassswordEditor/PasswordEditorErrorsViewModel';
import { AccountPasswordProps } from './AccountPasswordProps';

const AccountInformationEditor: React.FC<WithTranslationProps & AccountPasswordProps & WithMessagesProps & WithNotificationsProps> = ({    
    id,
    isOpen,
    onClose,
    close,
    t,
    messages,
    notifications
}) => {
    const modelFields: Record<keyof PasswordEditorViewModel, string> = {
        id: 'id',
        lastPassword: 'lastPassword',
        newPassword: 'newPassword'
    }
    const initialModel: PasswordEditorViewModel = {
        id: 0,
        lastPassword: '',
        newPassword: ''
    }
    const [model, setModel] = useState<PasswordEditorViewModel>(initialModel);
    const [errors, setErrors] = useState<PasswordEditorErrorsViewModel>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);    

    const handleSave = async () => {
        try {
            setIsLoading(true);
            if (await validModel()) {
                const response = await axios_helper_async<boolean>("/Account/Update-Customer-Password", 'PATCH', model);

                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);

                if (response.isSuccess) {
                    if (response.data) {
                        notifications.showNotification("UserProfile.PasswordEditor.Successes.Save", NotificationType.Success);
                        close();
                    }
                    else {
                        messages.showMessage("", MessageType.Error, "UserProfile.PasswordEditor.Errors.Save");
                    }
                }
                else {
                    messages.showMessage("UserProfile.Errors.GetCustomerInformation", MessageType.Error, "Error");
                }
            }
        }
        catch (err: any) {
            notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
        }
        finally {
            setIsLoading(false);
        }
    }

    const verifyNewPassword = async (): Promise<boolean> => {
        let res: boolean = false;
        try {
            setIsLoading(true);
            if(model.newPassword){
                // const body: PasswordEditorViewModel = {
                //     id: model.id,
                //     newPassword: model.newPassword,
                //     lastPassword: '',
                // }
                const response = await axios_helper_async<boolean>("/Account/Verify-New-Password", 'POST', model);
    
                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);
    
                if (response.isSuccess) {
                    if(response.data) {
                        res = response.data;
                    }
                }
                else {
                    messages.showMessage("UserProfile.Errors.GetCustomerInformation", MessageType.Error, "Error");
                }
            }
        }
        catch (err: any) {
            notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
        }
        finally {
            setIsLoading(false);            
        }
        return res;
    }
    const verifyLastPassword = async (): Promise<boolean> => {
        let res: boolean = false;
        try {
            setIsLoading(true);
            if(model.lastPassword){
                // const body = {
                //     password: model.lastPassword,
                //     id: model.id
                // }
                const response = await axios_helper_async<boolean>("/Account/Verify-Last-Password", 'POST', model);
    
                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);
    
                if (response.isSuccess) {
                    if(response.data) {
                        res = response.data;
                    }
                }
                else {
                    messages.showMessage("UserProfile.Errors.GetCustomerInformation", MessageType.Error, "Error");
                }
            }
        }
        catch (err: any) {
            notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
        }
        finally {
            setIsLoading(false);            
        }
        return res;
    }

    const validModel = async () => {
        let newErrors: PasswordEditorErrorsViewModel = {};

        if(!await verifyLastPassword()) newErrors.wrongLastPassword = "UserProfile.PasswordEditor.Errors.WrongLastPassword";
        if(await verifyNewPassword()) newErrors.noPasswordChange = "UserProfile.PasswordEditor.Errors.NoPasswordChange";
        if (!model.lastPassword) newErrors.lastPassword = "UserProfile.PasswordEditor.Errors.LastPassword";
        if (!model.newPassword) newErrors.newPassword = "UserProfile.PasswordEditor.Errors.NewPassword";
        if (model.lastPassword && model.newPassword && (model.lastPassword === model.newPassword)) newErrors.samePasswords = "UserProfile.PasswordEditor.Errors.SamePassword";

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setModel(prevModel => ({
            ...prevModel,
            [name]: value
        }));
    }

    useEffect(() => {
        if(id) {
            setModel(prevModel => ({
                ...prevModel,
                id: id
            }));
        }
    }, [id]);

    return (
        <React.Fragment>
            {
                isOpen ?
                    <div className="flex justify-center items-center overflow-hidden fixed inset-0 z-[1005] p-2 min-h-screen">
                        <div className="my-8 mx-auto max-w-sm sm:max-w-md md:max-w-lg z-[47] bg-white rounded-lg shadow-lg overflow-hidden w-full">
                            <div className="flex flex-col items-center p-6 text-center w-full gap-2">
                                <div className="flex flex-row justify-center items-center w-full">
                                    <div className="flex justify-center items-center w-full">
                                        {/* <h3 className="text-md font-bold text-black w-full">{t("Product.ProductRequest.Title")}</h3> */}
                                        <Subtitle text={t("UserProfile.PasswordEditor.Title")}></Subtitle>
                                    </div>
                                    {!isLoading ?
                                        <div className="flex justify-center items-center">
                                            <button onClick={close}><X></X></button>
                                        </div>
                                        :
                                        null
                                    }
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.PasswordEditor.LastPassword")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.lastPassword || errors.samePasswords || errors.wrongLastPassword ? "border-red-500" : null}`}
                                        id={modelFields.lastPassword}
                                        name={modelFields.lastPassword}
                                        value={model.lastPassword}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.PasswordEditor.LastPassword")}
                                    />
                                    {errors && errors.lastPassword && <div className="mt-1 text-sm text-red-500">{t(errors.lastPassword)}</div>}
                                    {errors && errors.wrongLastPassword && <div className="mt-1 text-sm text-red-500">{t(errors.wrongLastPassword)}</div>}
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.PasswordEditor.NewPassword")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.newPassword || errors.samePasswords || errors.noPasswordChange ? "border-red-500" : null}`}
                                        id={modelFields.newPassword}
                                        name={modelFields.newPassword}
                                        value={model.newPassword}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.PasswordEditor.NewPassword")}
                                    />
                                    {errors && errors.newPassword && <div className="mt-1 text-sm text-red-500">{t(errors.newPassword)}</div>}
                                    {errors && errors.samePasswords && <div className="mt-1 text-sm text-red-500">{t(errors.samePasswords)}</div>}
                                    {errors && errors.noPasswordChange && <div className="mt-1 text-sm text-red-500">{t(errors.noPasswordChange)}</div>}
                                </div>                                
                                {
                                    isLoading ?
                                        <LoadingButton />
                                        :
                                        <button className="btn btn-custom flex flex-row justify-center items-center gap-2 w-full" onClick={handleSave}>{t("UserProfile.PasswordEditor.Save")}</button>
                                }
                            </div>
                        </div>
                        <div className="opacity-50 fixed inset-0 z-[46] bg-black"></div>
                    </div>
                    :
                    null
            }
        </React.Fragment>
    );
}

export default withTranslation(withMessages(withNotifications(AccountInformationEditor)));